<template>
  <!-- `embedded` renders this view as a Reports tab body: no shell, no page heading of its own. -->
  <component :is="embedded ? 'div' : AppShell">
    <section class="view on soa-view-factcheck" :aria-labelledby="embedded ? undefined : 'fc-title'" :aria-label="embedded ? t('facts_h') : undefined">
      <div class="pagehead" :class="{ 'ph-embedded': embedded }">
        <div v-if="!embedded">
          <h1 id="fc-title">{{ t("facts_h") }}</h1>
          <p class="sub">{{ t("facts_sub") }}</p>
        </div>
        <button type="button" class="btn" :disabled="Boolean(busy)" @click="recheck">
          {{ busy === "recheck" ? "Re-reading…" : "Re-check now" }}
        </button>
      </div>

      <p v-if="!brandId" class="empty" role="status">Pick a brand to check.</p>
      <p v-else-if="loading" class="empty" role="status">Reading every surface…</p>
      <p v-else-if="unavailable" class="empty" role="status">Fact check is not switched on for this account yet.</p>
      <p v-else-if="error" class="empty" role="alert">
        {{ error }} <button type="button" class="atbtn" @click="load">Try again</button>
      </p>

      <template v-else>
        <!-- ── R11 · the five columns ─────────────────────────────────────────────────────── -->
        <section class="fc-sec card" aria-labelledby="fc-patrol-h">
          <div class="fc-sech">
            <h2 id="fc-patrol-h">Fact patrol</h2>
            <span class="fc-when">Checked {{ checkedAgo }}</span>
          </div>
          <p class="fc-note">Every column is compared against your own website — that is the one you control.</p>

          <div class="fc-tablewrap">
            <table class="fc-table">
              <caption class="sr-only">Five facts about your company, as stated on five surfaces.</caption>
              <thead>
                <tr>
                  <th scope="col">Fact</th>
                  <th v-for="column in FACT_COLUMNS" :key="column" scope="col">
                    {{ COLUMN_LABELS[column] }}
                    <span v-if="sourceLabel(patrol, column)" class="fc-src" :title="sourceReason(patrol, column)">{{ sourceLabel(patrol, column) }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="fact in facts" :key="fact.key">
                  <th scope="row" data-label="Fact">{{ fact.label }}</th>
                  <td
                    v-for="column in FACT_COLUMNS"
                    :key="column"
                    class="fc-cell"
                    :class="`fc-is-${cellState(fact, column, patrol)}`"
                    :data-label="COLUMN_LABELS[column]"
                  >
                    <span class="fc-dot" aria-hidden="true"></span>
                    <span class="fc-val">{{ cellText(fact, column) }}</span>
                    <span class="sr-only">{{ STATE_WORDS[cellState(fact, column, patrol)] }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <ul class="fc-legend">
            <li v-for="state in CELL_STATES" :key="state" :class="`fc-is-${state}`"><span class="fc-dot" aria-hidden="true"></span>{{ STATE_WORDS[state] }}</li>
          </ul>

          <template v-if="issues.length">
            <h3 class="fc-h3">{{ issues.length }} to fix</h3>
            <ul class="fc-issues">
              <li v-for="issue in issues" :key="issue.id" class="fc-issue">
                <p class="fc-issue-t">{{ issue.text }}</p>
                <div class="fc-issue-acts">
                  <button v-if="!planRouteOff" type="button" class="pl-go" :disabled="busy === `plan-${issue.id}` || planned.includes(issue.id)" @click="addToPlan(issue)">
                    {{ planned.includes(issue.id) ? "On the plan" : busy === `plan-${issue.id}` ? "Adding…" : "Add to Plan" }}
                  </button>
                  <button type="button" class="pl-go ghost" :aria-expanded="openIssue === issue.id ? 'true' : 'false'" @click="openIssue = openIssue === issue.id ? '' : issue.id">
                    {{ openIssue === issue.id ? "Hide fix" : "Show fix" }}
                  </button>
                </div>
                <p v-if="planError[issue.id]" class="fc-error" role="alert">{{ planError[issue.id] }}</p>
                <div v-if="openIssue === issue.id" class="fc-fix">
                  <p class="fc-fix-t">{{ issue.fix }}</p>
                  <div class="fc-issue-acts">
                    <button type="button" class="atbtn" @click="copy(issue.fix)">{{ copied === issue.fix ? "Copied" : "Copy this text" }}</button>
                    <button type="button" class="atbtn" @click="askMaya(issue)">Ask Maya</button>
                  </div>
                </div>
              </li>
            </ul>
          </template>
          <p v-else class="fc-clear">Nothing on those surfaces disagrees with your website right now.</p>

          <p v-if="carried.length" class="fc-note fc-carried">
            {{ carried.length }} earlier {{ carried.length === 1 ? "issue is" : "issues are" }} carried forward: we could not re-read the page {{ carried.length === 1 ? "it lives" : "they live" }} on, so we are not calling {{ carried.length === 1 ? "it" : "them" }} fixed.
          </p>
        </section>

        <!-- ── R10 · the client's own website ─────────────────────────────────────────────── -->
        <!-- Nothing at all unless R10 answered: an empty section here reads as findings about the
             client's site, and "not switched on" is not a finding. -->
        <section v-if="check || entityOff" class="fc-sec card" aria-labelledby="fc-entity-h">
          <div class="fc-sech">
            <h2 id="fc-entity-h">Website check</h2>
            <span v-if="check?.website?.url" class="fc-when">{{ check.website.url }}</span>
          </div>

          <p v-if="entityOff" class="fc-note">Website check is not switched on for this workspace.</p>

          <template v-else>
          <p v-if="websiteUnreadable(check)" class="fc-warn" role="status">
            We could not read your website{{ check?.website?.reason ? ` — ${check.website.reason}` : "" }}. Nothing below is a verdict on your site.
          </p>

          <template v-for="segment in SEGMENTS" :key="segment.key">
            <template v-if="segments[segment.key].length">
              <h3 class="fc-h3">{{ segment.label }}</h3>
              <ul class="fc-facts">
                <li v-for="fact in segments[segment.key]" :key="fact.key" class="fc-frow">
                  <div class="fc-fmain">
                    <span class="fc-flab">{{ entityFactLabel(fact) }}</span>
                    <span class="fc-fdet">{{ entityDetail(fact) }}</span>
                    <a v-if="safeHref(entityColumnUrl(fact, 'website'))" class="fc-fsrc" :href="safeHref(entityColumnUrl(fact, 'website'))" target="_blank" rel="noopener">Source</a>
                  </div>
                  <div v-if="segment.key !== 'agreed'" class="fc-issue-acts">
                    <button type="button" class="pl-go ghost" @click="startFix(fact)">Fix on my GEO site</button>
                  </div>
                  <form v-if="fixing === fact.key" class="fc-fixform" @submit.prevent="submitFix(fact)">
                    <label class="sr-only" :for="`fix-${fact.key}`">{{ entityFactLabel(fact) }}</label>
                    <input :id="`fix-${fact.key}`" v-model="fixValue" class="fc-input" type="text" autocomplete="off" placeholder="What it should say">
                    <button type="submit" class="pl-go" :disabled="busy === `fix-${fact.key}`">{{ busy === `fix-${fact.key}` ? "Publishing…" : "Put it on my GEO site" }}</button>
                    <button type="button" class="atbtn" @click="fixing = ''">Cancel</button>
                  </form>
                </li>
              </ul>
            </template>
          </template>

          <p v-if="fixNotice" class="fc-notice" role="status">{{ fixNotice }}</p>
          <p v-if="fixError" class="fc-error" role="alert">{{ fixError }}</p>

          <h3 class="fc-h3">Profiles AI can tie to you</h3>
          <ul class="fc-plats">
            <li v-for="platform in check?.same_as || []" :key="platform.platform + platform.url" class="fc-plat" :class="platform.present ? 'is-on' : 'is-off'">
              <span class="fc-dot" aria-hidden="true"></span>
              <span class="fc-platname">{{ platform.platform }}</span>
              <a v-if="safeHref(platform.url)" :href="safeHref(platform.url)" target="_blank" rel="noopener" class="fc-platurl">{{ platform.url }}</a>
              <!-- A URL we will not link to is still a URL the client has on file: show it, do not
                   call it missing. -->
              <span v-else-if="platform.url" class="fc-platurl">{{ platform.url }}</span>
              <span v-else class="fc-platurl fc-platmiss">Not linked</span>
            </li>
          </ul>

          <h3 class="fc-h3">Link back from your website</h3>
          <p class="fc-note">
            <template v-if="check?.backlink?.state === 'found'">Your website links to your GEO site — that is the handshake AI needs.</template>
            <template v-else>Your website does not link to your GEO site yet, so the two are not tied together.</template>
          </p>
          <div v-if="check?.backlink?.snippet" class="fc-snippet">
            <code>{{ check.backlink.snippet }}</code>
            <button type="button" class="atbtn" @click="copy(check.backlink.snippet)">{{ copied === check.backlink.snippet ? "Copied" : "Copy" }}</button>
          </div>
          </template>
        </section>
      </template>
      <p class="sr-only" role="status" aria-live="polite">{{ announcement }}</p>
    </section>
  </component>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { addIssueToPlan, fixEntityFact, getEntityCheck, getFactPatrol, recheckEntityCheck, recheckFactPatrol } from "../api/geoExec";
import {
  CELL_STATES,
  COLUMN_LABELS,
  FACT_COLUMNS,
  ago,
  carriedIssues,
  cellState,
  cellValue,
  entityColumnUrl,
  entityColumnValue,
  entityDetail,
  entityFactLabel,
  entitySegments,
  openIssues,
  safeHref,
  sourceLabel,
  sourceReason,
  websiteUnreadable,
} from "../composables/factCheck";
import AppShell from "../layout/AppShell.vue";
import { currentBrand, loadBrands } from "../stores/brands";
import { featureHidden, featureIsOff, noteFeature } from "../stores/geoExecFeatures";
import { useI18n } from "../useI18n";

const PLAN_ROUTE_KEY = "soa_r11_plan_route";

function readPlanRouteOff() {
  try {
    return sessionStorage.getItem(PLAN_ROUTE_KEY) === "off";
  } catch {
    return false; // private mode: ask again rather than hide a working button
  }
}

const STATE_WORDS = {
  match: "Agrees",
  mismatch: "Disagrees",
  missing: "Not stated",
  unreadable: "Not read",
};

const SEGMENTS = [
  { key: "attention", label: "Needs your attention" },
  { key: "unstated", label: "Not stated yet" },
  { key: "agreed", label: "Agreed everywhere" },
];

const props = defineProps({ embedded: { type: Boolean, default: false } });

const { t } = useI18n();
const router = useRouter();
const patrol = ref(null);
const check = ref(null);
const entityOff = ref(false);  // R10 off while R11 is on
const loading = ref(true);
const error = ref("");
const busy = ref("");
const openIssue = ref("");
const fixing = ref("");
const fixValue = ref("");
const fixNotice = ref("");
const fixError = ref("");
const copied = ref("");
const announcement = ref("");
// FE-5 · one mismatch, one plan row. Kept per issue id rather than as a flag so a second row can
// be added while the first is still going out.
const planned = ref([]);
const planError = reactive({});
// The "add this one now" route is not deployed everywhere yet (see `addIssueToPlan`). One 404 is
// the deployment answering, so the button goes away for the session rather than failing per row —
// the same rule `stores/geoExecFeatures` applies to a switched-off feature.
const planRouteOff = ref(readPlanRouteOff());
let controller;

const brandId = computed(() => currentBrand.value?.id || null);
const unavailable = computed(() => featureHidden("factCheck"));
const facts = computed(() => (Array.isArray(patrol.value?.facts) ? patrol.value.facts : []));
const issues = computed(() => openIssues(patrol.value));
const carried = computed(() => carriedIssues(patrol.value));
const segments = computed(() => entitySegments(check.value));
const checkedAgo = computed(() => ago(patrol.value?.checked_at));

function cellText(fact, column) {
  const state = cellState(fact, column, patrol.value);
  if (state === "unreadable") return sourceLabel(patrol.value, column) || "Not read";
  return cellValue(fact, column) || "—";
}

async function load({ recheck = false } = {}) {
  if (!brandId.value || unavailable.value) {
    patrol.value = null;
    check.value = null;
    loading.value = false;
    return;
  }
  controller?.abort();
  controller = new AbortController();
  const signal = controller.signal;
  if (!recheck) {
    // The new brand's screen starts empty. Leaving the previous brand's table up while the next
    // one loads puts one company's facts under another company's name — and if the new brand
    // fails, leaves them there for good.
    patrol.value = null;
    check.value = null;
    entityOff.value = false;
    loading.value = true;
  }
  error.value = "";
  const [patrolResult, checkResult] = await Promise.allSettled([
    recheck ? recheckFactPatrol(brandId.value, { signal }) : getFactPatrol(brandId.value, { signal }),
    recheck ? recheckEntityCheck(brandId.value, { signal }) : getEntityCheck(brandId.value, { signal }),
  ]);
  // Aborted means a newer load owns this screen. Everything below writes shared state, so a
  // superseded request must stop here — including `loading`, which it would otherwise switch off
  // over the next brand's blank page.
  if (signal.aborted) return;
  loading.value = false;
  // One surface failing must not blank the other: the two halves of this page are two calls and
  // they fail separately (R10's own `fetch_failed` degrades one card, never the screen).
  if (patrolResult.status === "fulfilled") {
    noteFeature("factCheck", null);
    patrol.value = patrolResult.value?.patrol || null;
  } else if (noteFeature("factCheck", patrolResult.reason)) {
    return;
  }
  if (checkResult.status === "fulfilled") {
    check.value = checkResult.value?.check || null;
  } else if (featureIsOff(checkResult.reason)) {
    // R10's own switch is off while R11's is on. Saying so is the only honest option: drawing the
    // section from a null check would report "your website does not link to your GEO site" about
    // a check that never ran.
    entityOff.value = true;
  }
  if (!patrol.value && !check.value) {
    const reason = [patrolResult, checkResult].find((r) => r.status === "rejected")?.reason;
    if (reason?.name === "AbortError") return;
    error.value = reason?.message || "We could not read your facts right now.";
  }
}

async function recheck() {
  busy.value = "recheck";
  announcement.value = "Re-reading every surface.";
  try {
    await load({ recheck: true });
    announcement.value = "Fact check updated.";
  } finally {
    busy.value = "";
  }
}

function startFix(fact) {
  fixing.value = fact.key;
  fixValue.value = entityColumnValue(fact, "website");
  fixNotice.value = "";
  fixError.value = "";
}

async function submitFix(fact) {
  busy.value = `fix-${fact.key}`;
  fixNotice.value = "";
  fixError.value = "";
  try {
    const result = await fixEntityFact(brandId.value, fact.key, fixValue.value);
    fixing.value = "";
    fixNotice.value = result?.changed
      ? `Your GEO site now says “${fixValue.value}”.`
      : "Your GEO site already said that, so nothing changed.";
    await load({ recheck: false });
  } catch (caught) {
    if (caught.status === 402) {
      // Same shape as R18's paywall: say why, change nothing.
      fixError.value = caught.payload?.message || "Correcting your GEO site is an Autopilot action.";
    } else {
      fixError.value = caught.payload?.message || caught.message || "That did not go through.";
    }
  } finally {
    busy.value = "";
  }
}

async function copy(text) {
  try {
    await navigator.clipboard.writeText(String(text || ""));
    copied.value = text;
    announcement.value = "Copied.";
    window.setTimeout(() => { if (copied.value === text) copied.value = ""; }, 2000);
  } catch {
    copied.value = ""; // no clipboard permission: the text is on screen and can be selected
  }
}

/** The client's move on a disagreement is to put it on the plan, not to be shown a paragraph. */
async function addToPlan(issue) {
  busy.value = `plan-${issue.id}`;
  planError[issue.id] = "";
  try {
    await addIssueToPlan(brandId.value, issue.id);
    planned.value = [...planned.value, issue.id];
    announcement.value = "Added to your plan.";
  } catch (caught) {
    // 409 already_planned is not a failure — the row the client wanted is already there.
    if (caught?.status === 409 && String(caught.payload?.error || "") === "already_planned") {
      planned.value = [...planned.value, issue.id];
    } else if (caught?.status === 404 || caught?.status === 405) {
      // The route is not there. That is a fact about the deployment, not about this row, so it
      // takes the button away instead of printing an error the client cannot act on.
      planRouteOff.value = true;
      try { sessionStorage.setItem(PLAN_ROUTE_KEY, "off"); } catch { /* storage is optional */ }
    } else {
      planError[issue.id] = caught?.payload?.msg || caught?.payload?.message || caught?.message || "That did not go through.";
    }
  } finally {
    busy.value = "";
  }
}

function askMaya(issue) {
  router.push({ path: "/maya", query: { ask: `${issue.text} How should I fix it?` } });
}

onMounted(async () => {
  // As a Reports tab the title belongs to Reports; only the standalone route names itself.
  if (!props.embedded) document.title = `${t("facts_h")} · SoaRank`;
  if (!currentBrand.value) await loadBrands().catch(() => {});
  await load();
});
// Issue ids repeat across brands (`founded:ai_says` is every brand's id for that mismatch), so a
// carried-over "On the plan" would be a claim about the wrong company. A same-brand reload —
// a re-check, or a fix that was just published — keeps them.
watch(brandId, () => {
  planned.value = [];
  for (const key of Object.keys(planError)) delete planError[key];
  load();
});
onBeforeUnmount(() => controller?.abort());
</script>

<style scoped>
/* Embedded, the tab rail above already names the section, so the head keeps only its button. */
.ph-embedded { justify-content: flex-end; margin-bottom: 0; }

/* `Add to Plan` is a plain `.pl-go` — the app's one filled button — so the only thing added here
   is what a disabled one looks like, which `.pl-go` does not say. */
.pl-go[disabled] {
  background: var(--fill);
  color: var(--mute);
  cursor: default;
}

.fc-sec {
  margin-bottom: 22px;
  padding: var(--card-pad-y) var(--card-pad-x);
}

.fc-sech {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.fc-sech h2 {
  margin: 0;
  font-size: var(--fs-title3);
}

.fc-when {
  color: var(--mute);
  font-size: var(--fs-caption);
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}

.fc-note {
  margin: 6px 0 14px;
  color: var(--mute);
  font-size: var(--fs-caption);
}

.fc-h3 {
  margin: 20px 0 8px;
  font-size: var(--fs-caption);
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--mute);
}

/* ── the five-column table ─────────────────────────────────────────────────────────────────── */

.fc-tablewrap {
  overflow-x: auto;
}

.fc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-caption);
}

.fc-table th,
.fc-table td {
  padding: 9px 10px;
  text-align: left;
  vertical-align: top;
  border-bottom: 1px solid var(--separator);
}

.fc-table thead th {
  color: var(--mute);
  font-weight: 650;
  white-space: nowrap;
}

.fc-table tbody th {
  font-weight: 700;
  color: var(--ink);
  white-space: nowrap;
}

.fc-src {
  display: block;
  margin-top: 2px;
  color: var(--verm);
  font-weight: 600;
  font-size: var(--fs-caption2);
}

.fc-cell {
  color: var(--body);
}

.fc-val {
  overflow-wrap: anywhere;
}

.fc-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 7px;
  border-radius: 50%;
  background: var(--mute);
  vertical-align: 1px;
}

.fc-is-match .fc-dot { background: var(--jade); }
.fc-is-mismatch .fc-dot { background: var(--verm); }
.fc-is-missing .fc-dot { background: var(--amber); }
.fc-is-unreadable .fc-dot { background: transparent; box-shadow: inset 0 0 0 1.5px var(--mute); }

.fc-is-mismatch { color: var(--verm); font-weight: 650; }
.fc-is-unreadable { color: var(--mute); }

.fc-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
  color: var(--mute);
  font-size: var(--fs-caption2);
}

/* ── issues and fixes ──────────────────────────────────────────────────────────────────────── */

.fc-issues,
.fc-facts,
.fc-plats {
  margin: 0;
  padding: 0;
  list-style: none;
}

.fc-issue,
.fc-frow {
  padding: 10px 0;
  border-bottom: 1px solid var(--separator);
}

.fc-issue-t {
  margin: 0 0 8px;
  color: var(--ink);
  font-size: var(--fs-caption);
}

.fc-issue-acts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.fc-issue-acts .pl-go,
.fc-fixform .pl-go,
.fc-issue-acts .atbtn {
  min-height: 34px;
  transition: transform 100ms ease-out;
}

.fc-issue-acts .pl-go:active,
.fc-issue-acts .atbtn:active {
  transform: scale(0.97);
}

.fc-fix {
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid var(--tint-line);
  border-radius: 10px;
  background: var(--tint-weak);
}

.fc-fix-t {
  margin: 0 0 8px;
  color: var(--body);
  font-size: var(--fs-caption);
}

.fc-fmain {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
}

.fc-flab {
  font-weight: 700;
  color: var(--ink);
  font-size: var(--fs-caption);
}

.fc-fdet {
  flex: 1 1 220px;
  color: var(--mute);
  font-size: var(--fs-caption);
}

.fc-fsrc {
  color: var(--indigo);
  font-size: var(--fs-caption2);
}

.fc-fixform {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.fc-input {
  flex: 1 1 220px;
  min-width: 0;
  min-height: 36px;
  padding: 7px 11px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--card);
  color: var(--ink);
  font: inherit;
  font-size: var(--fs-caption);
}

.fc-warn,
.fc-notice,
.fc-error {
  margin: 10px 0 0;
  font-size: var(--fs-caption);
}

.fc-warn { color: var(--amber); }
.fc-notice { color: var(--jade); }
.fc-error { color: var(--verm); font-weight: 650; }

.fc-clear {
  margin: 8px 0 0;
  color: var(--jade);
  font-size: var(--fs-caption);
}

.fc-carried {
  margin-top: 10px;
}

/* ── profiles and the footer link ──────────────────────────────────────────────────────────── */

.fc-plat {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 7px 0;
  border-bottom: 1px solid var(--separator);
  font-size: var(--fs-caption);
}

.fc-plat.is-on .fc-dot { background: var(--jade); }
.fc-plat.is-off .fc-dot { background: transparent; box-shadow: inset 0 0 0 1.5px var(--mute); }

.fc-platname {
  min-width: 96px;
  font-weight: 650;
  color: var(--ink);
  text-transform: capitalize;
}

.fc-platurl {
  overflow-wrap: anywhere;
  color: var(--indigo);
}

.fc-platmiss {
  color: var(--mute);
}

.fc-snippet {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--fill-4);
}

.fc-snippet code {
  flex: 1 1 260px;
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: "SF Mono", ui-monospace, Menlo, monospace;
  font-size: var(--fs-caption2);
  color: var(--body);
}

/* ── narrow screens: one fact per card, one column per line ────────────────────────────────── */

@media (max-width: 760px) {
  .fc-sec {
    padding: 18px 16px;
  }

  .fc-tablewrap {
    overflow-x: visible;
  }

  .fc-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }

  .fc-table,
  .fc-table tbody,
  .fc-table tr,
  .fc-table th,
  .fc-table td {
    display: block;
    width: auto;
  }

  .fc-table tr {
    margin-bottom: 12px;
    border: 1px solid var(--line);
    border-radius: 10px;
    overflow: hidden;
  }

  .fc-table tbody th {
    background: var(--fill-4);
    border-bottom: 1px solid var(--separator);
  }

  /* label · dot · value on ONE line: three tracks, because the dot is its own element and a
     two-track grid pushes the value onto a second row under it. */
  .fc-table td {
    display: grid;
    grid-template-columns: minmax(84px, 34%) auto 1fr;
    align-items: baseline;
    gap: 0 8px;
  }

  .fc-table td .fc-dot {
    margin-right: 0;
  }

  .fc-table td::before {
    content: attr(data-label);
    color: var(--mute);
    font-weight: 650;
  }

  .fc-table tr td:last-child {
    border-bottom: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fc-issue-acts .pl-go,
  .fc-issue-acts .atbtn {
    transition: none;
  }

  .fc-issue-acts .pl-go:active,
  .fc-issue-acts .atbtn:active {
    transform: none;
  }
}
</style>
